import { useCallback, useEffect, useMemo, useState } from 'react'
import invariant from 'tiny-invariant'
import {
  MapDataFieldRefFormDataField,
  MeetingSlotSelectedWithReservation,
  MeetingSlotSelectedWithoutReservation,
} from '@chilipiper/api-client/src/chat-platform-chat'
import { useMutation } from '@tanstack/react-query'
import { useRoomDataContext } from '../chat-data/RoomDataContext'
import { useXMPPClientContext } from '../xmpp-client/XMPPClientContext'
import { TypedEnvelopeOut } from '../xmpp-client/types'
import { usePrepareChat } from '../api-queries/chat-platform-chat'
import { useThrottledBlocks } from './useThrottledBlocks'
import { getPrepareChatData, setPrepareChatData, syncStoredGuestId } from './helpers'

interface Params {
  onBootstrapComplete?: (roomId: string, playbookId: string) => void
  tenantId?: string
  workspaceId?: string
}
export const useMessageFeed = ({ workspaceId, tenantId, onBootstrapComplete }: Params) => {
  const [roomId, setRoomId] = useState<string>()
  const guestId = useMemo(() => {
    if (workspaceId && tenantId) {
      return syncStoredGuestId(workspaceId, tenantId)
    }
  }, [workspaceId, tenantId])

  const { mutateAsync: prepareChatAsync } = usePrepareChat(tenantId ?? '')

  const {
    client: { start, stop, sendEvent, status, loadAffiliations },
  } = useXMPPClientContext()

  const { events, isInitial } = useRoomDataContext()
  const sendingMessage = useMemo(() => events.some(event => !event.sent), [events])

  const lastBlock = events.at(-1)
  const liveChatActive =
    lastBlock?.type === 'RouteToRep' ||
    lastBlock?.type === 'StatusChange' ||
    lastBlock?.type === 'LiveMessage' ||
    lastBlock?.type === 'SuggestedTimes' ||
    (lastBlock?.type === 'SystemMessage' &&
      (lastBlock.payload.type === 'SystemConversationReassigned' ||
        lastBlock.payload.type === 'SystemConversationAssigned'))

  const piperAiActive =
    lastBlock?.type === 'AiResponse' ||
    lastBlock?.type === 'MessageToPiperAi' ||
    lastBlock?.type === 'EnterPiperAiChat'

  const mergedMessageNodesIntoMessagePartsEvents = useMemo(() => {
    const mergedEvents: typeof events = []
    for (const event of events) {
      const lastEvent = mergedEvents.at(-1)
      if (event.type === 'Message' && lastEvent?.type === 'Message') {
        mergedEvents[mergedEvents.length - 1] = {
          ...lastEvent,
          parts: [...lastEvent.parts, ...event.parts],
        }
      } else {
        mergedEvents.push(event)
      }
    }
    return mergedEvents
  }, [events])

  const { throttledBlocks, isThrottling } = useThrottledBlocks(
    mergedMessageNodesIntoMessagePartsEvents,
    liveChatActive,
    isInitial
  )

  const sendLiveEvent = useCallback(
    (content: Extract<TypedEnvelopeOut, { type: 'LiveEnvelope' }>['payload']) => {
      if (roomId) {
        return sendEvent(
          roomId,
          {
            createdAt: new Date().toISOString(),
            type: 'LiveEnvelope',
            payload: content,
            protocolVersion: 1,
          },
          true
        )
      }
    },
    [roomId, sendEvent]
  )

  const sendBotEvent = useCallback(
    (
      content: Extract<TypedEnvelopeOut, { type: 'BotEnvelope' }>['payload'],
      optimisticUpdate?: boolean
    ) => {
      if (roomId) {
        return sendEvent(
          roomId,
          {
            createdAt: new Date().toISOString(),
            type: 'BotEnvelope',
            payload: content,
            protocolVersion: 1,
          },
          optimisticUpdate
        )
      }
    },
    [roomId, sendEvent]
  )

  const pickReplyButton = useCallback(
    (replyIndex: number) => {
      return sendBotEvent({ type: 'ReplyButtonSelected', index: replyIndex })
    },
    [sendBotEvent]
  )

  const selectMeetingSlot = useCallback(
    (data: MeetingSlotSelectedWithReservation | MeetingSlotSelectedWithoutReservation) => {
      return sendBotEvent(data)
    },
    [sendBotEvent]
  )

  const triggerMeetingFallback = useCallback(() => {
    return sendBotEvent({ type: 'MeetingSlotSelectionCancelled' })
  }, [sendBotEvent])

  const submitForm = useCallback(
    (values: MapDataFieldRefFormDataField) => {
      return sendBotEvent({ type: 'FormData', values }, true)
    },
    [sendBotEvent]
  )

  const sendLiveMessage = useCallback(
    (text: string) => {
      return sendLiveEvent({
        type: 'LiveMessage',
        parts: [{ type: 'Text', text }],
      })
    },
    [sendLiveEvent]
  )

  const sendMessageToPiperAi = useCallback(
    (text: string) => {
      return sendBotEvent({ type: 'MessageToPiperAi', value: { content: text } }, true)
    },
    [sendBotEvent]
  )

  useEffect(() => {
    return () => {
      stop()
    }
  }, [stop])

  const { isPending: isLoading, mutateAsync: prepareChat } = useMutation({
    mutationFn: async ({
      tenantId,
      workspaceId,
      guestId,
      playbookId,
    }: {
      guestId: string
      playbookId: string
      tenantId: string
      workspaceId: string
    }) => {
      if (!tenantId || !workspaceId || !guestId) {
        return
      }
      let roomId: string | undefined

      const prepare = async (isRetrying: boolean) => {
        const cachedPrepareChatData = getPrepareChatData(guestId, workspaceId)
        if (cachedPrepareChatData && !isRetrying) {
          return cachedPrepareChatData
        }
        const data = await prepareChatAsync({
          guestId,
          workspaceId,
        })
        setPrepareChatData(guestId, workspaceId, data)
        return data
      }
      await start(tenantId, workspaceId, async isRetrying => {
        const { refreshToken, roomJid, guestJid, authorizationHeader } = await prepare(isRetrying)
        const userAccessToken = window.atob(authorizationHeader.replace('Bearer ', ''))
        const userRefreshToken = refreshToken
          ? window.atob(refreshToken.replace('Bearer ', ''))
          : undefined
        const newRoomId = roomJid.split('@')[0]
        invariant(newRoomId, 'Room ID must be defined')
        roomId = newRoomId
        onBootstrapComplete?.(roomId, playbookId)
        setRoomId(roomId)
        return {
          credentials: {
            username: guestJid,
            token: userAccessToken,
            refreshToken: userRefreshToken,
          },
          roomId,
        }
      })
      if (roomId) {
        await loadAffiliations({ roomId })
      }
    },
  })

  return {
    selectMeetingSlot,
    guestId,
    liveChatActive,
    sendingMessage,
    pickReplyButton,
    submitForm,
    blocks: throttledBlocks,
    isThrottling,
    sendLiveMessage,
    prepareChat,
    isLoading,
    status,
    roomId,
    triggerMeetingFallback,
    piperAiActive,
    sendMessageToPiperAi,
  }
}
